import React, { useState, Fragment, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { Button, TextField, IconButton } from "@mui/material";
import { TrashIcon, QrCodeIcon, MagnifyingGlassIcon, PlusIcon, XMarkIcon } from "@heroicons/react/24/outline";
import ModernProductSelectorV2 from "../../../utils/Selector/ModernProductSelectorV2";
import { fetchProducts } from "../../../../api/product";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import QRCodeScanner from "../../../utils/QRCodeScanner";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

const CountAddItemsDialog = ({ open, onClose, onSave, countId, onSubDialogStateChange }) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([
    {
      id: uuidv4(),
      product_id: null,
      product: null,
      articleNumber: "",
      description: "",
      quantity: "",
      purchase_price: "",
      note: "",
    },
  ]);
  const [productSelectorOpen, setProductSelectorOpen] = useState(false);
  const [selectedItemRow, setSelectedItemRow] = useState(null);
  const [qrScanMode, setQrScanMode] = useState(false);
  const [qrCodeInput, setQrCodeInput] = useState("");
  const [searchingItems, setSearchingItems] = useState(new Set()); // Track which items are currently being searched
  const debounceTimers = useRef({}); // Store debounce timers per item

  // Track if product selector is open and notify parent
  useEffect(() => {
    if (onSubDialogStateChange) {
      onSubDialogStateChange(productSelectorOpen || qrScanMode);
    }
  }, [productSelectorOpen, qrScanMode, onSubDialogStateChange]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setItems([
        {
          id: uuidv4(),
          product_id: null,
          product: null,
          articleNumber: "",
          description: "",
          quantity: "",
          purchase_price: "",
          note: "",
        },
      ]);
      setQrScanMode(false);
      setQrCodeInput("");
      setSelectedItemRow(null);
      setProductSelectorOpen(false);
      setSearchingItems(new Set());
      // Clear all debounce timers
      Object.values(debounceTimers.current).forEach((timer) => clearTimeout(timer));
      debounceTimers.current = {};
    }
  }, [open]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      Object.values(debounceTimers.current).forEach((timer) => clearTimeout(timer));
    };
  }, []);

  // Auto-add new row when any row is filled
  useEffect(() => {
    if (!open) return;

    // Check if any row has been filled (has product OR description, and quantity)
    const hasFilledRow = items.some((item) => {
      const hasProduct = item.product_id || item.product;
      const hasDescription = item.description && item.description.trim() !== "";
      const hasQuantity = item.quantity && parseFloat(item.quantity) > 0;
      return (hasProduct || hasDescription) && hasQuantity;
    });

    // If we have a filled row, check if we need to add a new empty row
    if (hasFilledRow && items.length > 0) {
      const hasEmptyRow = items.some(
        (item) =>
          (!item.product_id && !item.product && (!item.description || item.description.trim() === "")) ||
          (!item.quantity || parseFloat(item.quantity) === 0)
      );

      // If no empty row exists, add one
      if (!hasEmptyRow) {
        setItems((prevItems) => [
          ...prevItems,
          {
            id: uuidv4(),
            product_id: null,
            product: null,
            articleNumber: "",
            description: "",
            quantity: "",
            purchase_price: "",
            note: "",
          },
        ]);
      }
    }
  }, [items, open]);

  const handleArticleNumberSearch = async (item, articleNumber) => {
    if (!articleNumber || articleNumber.trim() === "") {
      setSearchingItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
      return;
    }

    // Mark this item as being searched
    setSearchingItems((prev) => new Set(prev).add(item.id));

    try {
      const response = await fetchProducts({
        query: articleNumber.trim(),
        page: 1,
        per_page: 10,
        fields: "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price",
      });

      // Check if the item still has the same article number (user might have continued typing)
      // Use functional state update to get the latest items
      setItems((prevItems) => {
        const currentItem = prevItems.find((i) => i.id === item.id);
        if (!currentItem || currentItem.articleNumber.trim() !== articleNumber.trim()) {
          // User has continued typing, ignore this result
          setSearchingItems((prev) => {
            const next = new Set(prev);
            next.delete(item.id);
            return next;
          });
          return prevItems; // Return unchanged
        }

        if (response && response.data && response.data.length > 0) {
          // Check for exact match on item_number
          const exactMatch = response.data.find(
            (p) =>
              p.item_number &&
              p.item_number.toLowerCase() === articleNumber.trim().toLowerCase()
          );

          if (exactMatch) {
            toast.success(t("inventory.count.product.found"));
            return prevItems.map((i) =>
              i.id === item.id
                ? {
                    ...i,
                    product_id: exactMatch.id,
                    product: exactMatch,
                    articleNumber: articleNumber.trim(),
                    purchase_price: exactMatch.purchase_price || "",
                    quantity: i.quantity || 1,
                  }
                : i
            );
          } else {
            // Open product selector if no exact match
            setSelectedItemRow(currentItem);
            setProductSelectorOpen(true);
            toast(t("inventory.count.product.no_exact_match"), { icon: "ℹ️" });
            return prevItems;
          }
        } else {
          // Only show error if the article number hasn't changed
          toast.error(t("inventory.count.product.not_found"));
          return prevItems;
        }
      });
    } catch (error) {
      console.error("Error searching product:", error);
      // Check if still current before showing error
      setItems((prevItems) => {
        const stillCurrent = prevItems.find((i) => i.id === item.id);
        if (stillCurrent && stillCurrent.articleNumber.trim() === articleNumber.trim()) {
          toast.error(t("inventory.count.product.search_error"));
        }
        return prevItems;
      });
    } finally {
      setSearchingItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleQrCodeScan = async (scannedCode) => {
    if (!scannedCode || scannedCode.trim() === "") {
      toast.error(t("inventory.count.qr.required"));
      return;
    }

    // Find first empty row or create new one
    let targetItem = items.find(
      (item) => !item.product_id && !item.product
    );
    if (!targetItem) {
      const newItem = {
        id: uuidv4(),
        product_id: null,
        product: null,
        articleNumber: "",
        description: "",
        quantity: "",
        purchase_price: "",
        note: "",
      };
      setItems([...items, newItem]);
      targetItem = newItem;
    }

    await handleArticleNumberSearch(targetItem, scannedCode.trim());
    setQrCodeInput("");
  };

  const handleItemChange = (itemId, field, value) => {
    setItems(
      items.map((item) =>
        item.id === itemId ? { ...item, [field]: value } : item
      )
    );
  };

  const handleRemoveRow = (itemId) => {
    if (items.length > 1) {
      setItems(items.filter((item) => item.id !== itemId));
    }
  };

  const handleProductSelect = (product) => {
    if (!selectedItemRow) return;

    setItems(
      items.map((item) =>
        item.id === selectedItemRow.id
          ? {
              ...item,
              product_id: product.id,
              product: product,
              articleNumber: product.item_number || "",
              purchase_price: product.purchase_price || "",
              quantity: item.quantity || 1,
            }
          : item
      )
    );
    setSelectedItemRow(null);
    setProductSelectorOpen(false);
  };

  const handleSave = async () => {
    // Filter out empty rows - allow items with either:
    // 1. Product (product_id or product object)
    // 2. Description + quantity (manual item)
    const validItems = items.filter((item) => {
      const hasProduct = item.product_id || item.product;
      const hasDescription = item.description && item.description.trim() !== "";
      const hasQuantity = item.quantity && parseFloat(item.quantity) > 0;
      
      // Valid if: (has product OR has description) AND has quantity
      return (hasProduct || hasDescription) && hasQuantity;
    });

    if (validItems.length === 0) {
      toast.error(t("inventory.count.bulk.no_valid_items"));
      return;
    }

    const itemsToAdd = validItems.map((item) => {
      // If product exists, use product data
      if (item.product_id || item.product) {
        return {
          product_id: item.product_id,
          quantity: parseFloat(item.quantity) || 1,
          purchase_price: item.purchase_price
            ? parseFloat(item.purchase_price)
            : (item.product?.purchase_price || 0),
          note: item.note || "",
        };
      } else {
        // Manual item with description only (not supported for counts, but keep for consistency)
        return {
          product_id: null,
          quantity: parseFloat(item.quantity) || 1,
          purchase_price: item.purchase_price ? parseFloat(item.purchase_price) : 0,
          note: item.note || item.description || "",
        };
      }
    });

    await onSave(itemsToAdd);
    onClose();
  };

  if (!open) return null;

  return (
    <>
      <Transition.Root show={open && !productSelectorOpen && !qrScanMode} as={Fragment}>
        <Dialog 
          as="div" 
          className="relative z-50" 
          onClose={(e) => {
            // Only close if product selector is not open
            if (!productSelectorOpen && !qrScanMode) {
              onClose();
            }
          }} 
          static
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-6xl sm:p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900">
                      {t("inventory.count.add_items_bulk")}
                    </Dialog.Title>
                    <button
                      onClick={() => {
                        if (!productSelectorOpen && !qrScanMode) {
                          onClose();
                        }
                      }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <XMarkIcon className="h-6 w-6" />
                    </button>
                  </div>



                  {/* Items table */}
                  <div className="max-h-96 overflow-y-auto border rounded">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width="5%"></TableCell>
                          <TableCell width="20%">{t("inventory.count.item_number")}</TableCell>
                          <TableCell width="25%">{t("inventory.count.product")}</TableCell>
                          <TableCell width="15%">{t("inventory.count.quantity")}</TableCell>
                          <TableCell width="15%">{t("inventory.count.purchase_price")}</TableCell>
                          <TableCell width="20%">{t("inventory.count.note")}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              {items.length > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleRemoveRow(item.id)}
                                  className="text-red-600"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={item.articleNumber}
                                onChange={(e) => {
                                  const newValue = e.target.value;
                                  handleItemChange(item.id, "articleNumber", newValue);
                                  
                                  // Clear existing debounce timer for this item
                                  if (debounceTimers.current[item.id]) {
                                    clearTimeout(debounceTimers.current[item.id]);
                                    delete debounceTimers.current[item.id];
                                  }

                                  // If product was found but user is changing the article number, clear the product
                                  if (item.product_id && newValue.trim() !== item.articleNumber.trim()) {
                                    handleItemChange(item.id, "product_id", null);
                                    handleItemChange(item.id, "product", null);
                                    handleItemChange(item.id, "purchase_price", "");
                                  }

                                  // Set up debounced search (wait 500ms after user stops typing)
                                  if (newValue.trim().length >= 2) {
                                    debounceTimers.current[item.id] = setTimeout(() => {
                                      handleArticleNumberSearch(item, newValue.trim());
                                      delete debounceTimers.current[item.id];
                                    }, 500);
                                  }
                                }}
                                placeholder={t("inventory.count.item_number_placeholder")}
                                onBlur={(e) => {
                                  // Clear any pending debounce timer
                                  if (debounceTimers.current[item.id]) {
                                    clearTimeout(debounceTimers.current[item.id]);
                                    delete debounceTimers.current[item.id];
                                  }
                                  
                                  // Search immediately on blur if there's a value and no product found yet
                                  if (e.target.value.trim() && !item.product_id && !searchingItems.has(item.id)) {
                                    handleArticleNumberSearch(item, e.target.value.trim());
                                  }
                                }}
                                disabled={searchingItems.has(item.id)}
                              />
                            </TableCell>
                            <TableCell>
                              {item.product ? (
                                <div className="text-sm">
                                  {item.product.displayTitle || item.product.title || `Produkt ${item.product.id}`}
                                </div>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<MagnifyingGlassIcon className="h-4 w-4" />}
                                  onClick={() => {
                                    setSelectedItemRow(item);
                                    setProductSelectorOpen(true);
                                  }}
                                >
                                  {t("inventory.count.select_product")}
                                </Button>
                              )}
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                type="number"
                                value={item.quantity}
                                onChange={(e) => handleItemChange(item.id, "quantity", e.target.value)}
                                inputProps={{ min: 0.01, step: 0.01 }}
                                placeholder="1"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                type="number"
                                value={item.purchase_price}
                                onChange={(e) => handleItemChange(item.id, "purchase_price", e.target.value)}
                                inputProps={{ min: 0, step: 0.01 }}
                                placeholder="0"
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                size="small"
                                fullWidth
                                value={item.note}
                                onChange={(e) => handleItemChange(item.id, "note", e.target.value)}
                                placeholder={t("inventory.count.note_placeholder")}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  <div className="mt-4 flex justify-end space-x-2">
                    <Button variant="outlined" onClick={onClose}>
                      {t("Cancel")}
                    </Button>
                    <Button variant="contained" onClick={handleSave}>
                      {t("inventory.count.add_items")} ({items.filter((item) => {
                        const hasProduct = item.product_id || item.product;
                        const hasDescription = item.description && item.description.trim() !== "";
                        const hasQuantity = item.quantity && parseFloat(item.quantity) > 0;
                        return (hasProduct || hasDescription) && hasQuantity;
                      }).length})
                    </Button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Product Selector */}
      {productSelectorOpen && (
        <Transition.Root show={productSelectorOpen} as={Fragment}>
          <Dialog as="div" className="relative z-50" static onClose={() => setProductSelectorOpen(false)}>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
            </Transition.Child>

            <div className="fixed inset-0 z-10 overflow-y-auto">
              <div className="flex min-h-full items-center justify-center text-center p-0 sm:p-4">
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                  enterTo="opacity-100 translate-y-0 sm:scale-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                  leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                >
                  <Dialog.Panel className="relative transform overflow-hidden bg-white text-left shadow-xl transition-all w-full max-w-md md:max-w-6xl lg:max-w-7xl h-screen max-h-screen rounded-none sm:rounded-md sm:h-auto sm:max-h-[85vh] flex flex-col min-h-0 p-0 sm:px-4 sm:py-4">
                    <div className="flex items-center justify-between flex-shrink-0 px-4 pt-4 pb-2 sm:px-0 sm:pt-0 sm:pb-0">
                      <Dialog.Title className="text-lg font-medium text-gray-900">
                        {t("inventory.count.select_product")}
                      </Dialog.Title>
                      <button
                        type="button"
                        onClick={() => {
                          setProductSelectorOpen(false);
                          setSelectedItemRow(null);
                        }}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                        aria-label="Lukk"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
                      <ModernProductSelectorV2
                        open={true}
                        selectedProducts={selectedItemRow?.product ? [selectedItemRow.product] : []}
                        onChange={(products) => {
                          if (products.length > 0) {
                            handleProductSelect(products[0]);
                          }
                        }}
                        onClose={() => {
                          setProductSelectorOpen(false);
                          setSelectedItemRow(null);
                        }}
                        multiple={false}
                        filters={[]}
                        showHeader={false}
                        showWarehouseOnlyDefault={true}
                        showQuantityInput={false}
                      />
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </Dialog>
        </Transition.Root>
      )}

      {/* QR Scanner */}
      {qrScanMode && (
        <QRCodeScanner
          open={qrScanMode}
          onScan={handleQrCodeScan}
          onBack={() => setQrScanMode(false)}
          placeholder={t("inventory.count.qr.placeholder")}
        />
      )}
    </>
  );
};

export default CountAddItemsDialog;
